/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { mediaTypes } from '../../registry/constants';
import { defaultValue } from './Media';

const MIN_WIDTH = 10;
const MAX_WIDTH = 1200;

const MIN_HEIGHT = 10;
const MAX_HEIGHT = 800;

const MediaManager: ComponentManagerFn<'Media'> = (
  platform: Platform,
): ComponentManager<'Media'> => {
  return {
    ...baseComponentManager<'Media'>(platform, 'Media'),
    type: 'Media',
    name: 'Media',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      src: {
        label: 'Src',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
        helpText: 'Provide the url for the media',
      },
      type: {
        label: 'Type',
        type: 'radio',
        options: mediaTypes,
        helpText: 'Select the type of the media',
      },
      viewAttribute: {
        label: 'View Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        helpText:
          'Choose an attribute that contains the value (URL) for the src attribute. Leave this blank and use the above Src property to specify an expression instead.',
      },
      mediaHeight: {
        label: 'Height',
        type: 'number',
        max: MAX_HEIGHT,
        min: MIN_HEIGHT,
        category: 'Layout',
      },
      mediaWidth: {
        label: 'Width',
        type: 'number',
        max: MAX_WIDTH,
        min: MIN_WIDTH,
        category: 'Layout',
      },
    },
    getDisplayText: () => 'Media',
    category: 'Forms',
    icon: { iconName: 'image' },
    description: `**Overview**<br/>
The Media component is a dynamic viewer designed to render external content such as images or web pages via IFrames. Unlike the **Image** component (which is for static system assets), the Media component is built for rendering content from URLs, making it ideal for displaying dynamic data, third-party embeds, or user-defined links.
Use this component when you need to show a product image from a URL stored in your database, or embed a YouTube video or Google Map directly into your page layout.

**1. Data Source (URL)**<br/>
The component requires a valid URL to render content. This can be provided in two ways:
* **View Attribute:** Binds to a Data Store field (String) that contains the URL. (Example: \`$.product.imageUrl\`).
* **Src (Expression):** Allows you to construct the URL dynamically using a formula. (Example: \`'https://www.youtube.com/embed/' + $.video.id\`).
* **Priority:** If **View Attribute** is left blank, the **Src** expression is used.

**2. Media Type**<br/>
You must specify what kind of content the URL points to so the browser knows how to render it.
* **Image:** Renders an \`<img>\` tag. Expects a direct link to an image file (jpg, png, svg).
* **IFrame:** Renders an \`<iframe>\`. Expects a link to a web page or embeddable widget.

**3. Dimensions**<br/>
Since external media can vary wildly in size, you must define the display constraints.
* **Height (mediaHeight):** Vertical size in pixels (Range: 10px–800px).
* **Width (mediaWidth):** Horizontal size in pixels (Range: 10px–1200px).

**4. Interactivity**<br/>
* **TriggerOnClick:** Supports the **TriggerOnClick** child component, allowing you to turn the media element into a clickable link or button.

**5. Best Use Cases**<br/>
* **Product Catalogs:** Displaying product photos where the image URLs are stored in an inventory database.
* **External Dashboards:** Embedding a PowerBI or Tableau report inside an IFrame.
* **Social Media Feeds:** Embedding a Twitter or LinkedIn widget.
* **Video Players:** Embedding instructional videos from Vimeo or YouTube using their embed URL format.`,
  };
};

export default MediaManager;
